import React, { useEffect, useState, useRef } from "react";
import api, { getBudgetSuggestions, getBudgetMonth, saveBudgetMonth } from "../api";
import { formatCurrency } from "../utils/formatCurrency";

export default function BudgetPlanner({ month, refreshKey, onUpdated }) {

  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [contributions, setContributions] = useState({ users: [] });
  const [incomes, setIncomes] = useState([]);
  const [budgetTarget, setBudgetTarget] = useState("");
  const [resteEditing, setResteEditing] = useState({}); // { userId: rawString } pendant la saisie
  const budgetTargetRef = useRef(budgetTarget);         // ref pour accès fiable dans les handlers async
  const [saveStatus, setSaveStatus] = useState(null);
  const [collapsedCats, setCollapsedCats] = useState(new Set());

  useEffect(() => {
    api.get('/budget/templates').then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    reloadPlanner();
    api.get(`/household-contributions/1/${month}`)
      .then(r => setContributions(r.data)).catch(() => {});
    api.get(`/household/1/income/average?month=${month}&months=12`)
      .then(r => setIncomes(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, refreshKey]);

  async function reloadPlanner() {
    setLoading(true);
    try {
      const [suggestions, currentBudget] = await Promise.all([
        getBudgetSuggestions(month),
        getBudgetMonth(month)
      ]);
      const suggestionList = Array.isArray(suggestions) ? suggestions : suggestions?.data || [];
      const raw = Array.isArray(currentBudget) ? currentBudget : currentBudget?.data;
      const budgetData = raw?.lines ?? (Array.isArray(raw) ? raw : []);
      if (raw?.budget_target != null) setBudgetTarget(raw.budget_target);

      // lignes sauvegardées pour ce mois
      const currentMap = {};
      budgetData.forEach(b => {
        if (b.planned_amount !== null && b.planned_amount !== undefined)
          currentMap[b.subcategory_id] = { amount: Number(b.planned_amount), is_manual: b.is_manual };
      });

      // si aucune ligne pour ce mois, chercher la dernière valeur sauvegardée
      // (is_manual=true → carry-forward, is_manual=false → l'utilisateur a choisi la suggestion)
      let prevManualMap = {};
      if (Object.keys(currentMap).length === 0) {
        try {
          const res = await api.get(`/budget/month/last-saved?month=${month}`);
          res.data.forEach(b => {
            if (b.is_manual && b.planned_amount !== null)
              prevManualMap[b.subcategory_id] = Number(b.planned_amount);
          });
        } catch {}
      }

      setLines(suggestionList.map(l => {
        const suggestion = Number(l.suggested_amount || 0);
        const current = currentMap[l.subcategory_id];
        if (current !== undefined) {
          return { ...l, suggested_amount: suggestion, planned_amount: current.amount, carried_forward: false };
        }
        if (prevManualMap[l.subcategory_id] !== undefined) {
          return { ...l, suggested_amount: suggestion, planned_amount: prevManualMap[l.subcategory_id], carried_forward: true };
        }
        return { ...l, suggested_amount: suggestion, planned_amount: suggestion, carried_forward: false };
      }));
    } catch (err) { console.error("Erreur chargement planner:", err); }
    finally { setLoading(false); }
  }

  function updateValue(index, value) {
    setLines(prev => {
      const next = [...prev];
      next[index] = { ...next[index], planned_amount: Number(value) };
      return next;
    });
  }

  function resetToSuggestion(index) {
    setLines(prev => {
      const next = [...prev];
      next[index] = { ...next[index], planned_amount: next[index].suggested_amount };
      return next;
    });
  }

  function resetAllToSuggestions() {
    setLines(prev => prev.map(l => ({ ...l, planned_amount: l.suggested_amount })));
  }

  async function loadTemplate(id) {
    if (!id) { setSelectedTemplate(""); return; }
    try {
      const res = await api.get(`/budget/template/${id}`);
      const templateMap = {};
      res.data.forEach(l => { templateMap[l.subcategory_id] = Number(l.planned_amount); });
      setLines(prev => prev.map(l => ({
        ...l,
        planned_amount: templateMap[l.subcategory_id] !== undefined ? templateMap[l.subcategory_id] : l.planned_amount
      })));
    } catch (err) { console.error("Erreur chargement template:", err); }
  }

  async function deleteTemplate() {
    if (!selectedTemplate) return;
    const t = templates.find(t => String(t.id) === String(selectedTemplate));
    if (!window.confirm(`Supprimer le template "${t?.name}" ?`)) return;
    try {
      await api.delete(`/budget/template/${selectedTemplate}`);
      setSelectedTemplate("");
      const r = await api.get('/budget/templates');
      setTemplates(r.data);
    } catch (err) { alert("Erreur suppression : " + (err.response?.data?.error || err.message)); }
  }

  async function refreshSuggestions() {
    try {
      const suggestions = await getBudgetSuggestions(month);
      const suggestionList = Array.isArray(suggestions) ? suggestions : suggestions?.data || [];
      setLines(prev => prev.map(l => {
        const updated = suggestionList.find(s => s.subcategory_id === l.subcategory_id);
        if (!updated) return l;
        const newSuggestion = Number(updated.suggested_amount || 0);
        return { ...updated, planned_amount: l.planned_amount, suggested_amount: newSuggestion, carried_forward: l.carried_forward };
      }));
    } catch {}
  }

  async function saveProvisionTarget(subcategoryId, value) {
    const numVal = value === '' || value === null ? null : parseFloat(value);
    try {
      await api.put(`/categories/subcategory/${subcategoryId}`, { provision_target: numVal });
      await refreshSuggestions();
    } catch (e) { console.error('Erreur sauvegarde objectif provision', e); }
  }

  async function saveProvisionStartDate(subcategoryId, value) {
    try {
      await api.put(`/categories/subcategory/${subcategoryId}`, { provision_start_date: value || null });
      await refreshSuggestions();
    } catch (e) { console.error('Erreur sauvegarde date démarrage provision', e); }
  }

  async function saveLoanField(subcategoryId, field, value) {
    const parsed = field === 'saving_target' ? (value === '' || value === null ? null : parseFloat(value)) : (value || null);
    try {
      await api.put(`/categories/subcategory/${subcategoryId}`, { [field]: parsed });
      await refreshSuggestions();
    } catch (e) { console.error('Erreur sauvegarde champ prêt', e); }
  }

  async function saveLines() {
    setSaveStatus("saving");
    try {
      const payload = lines.map(l => ({
        subcategory_id: l.subcategory_id,
        planned_amount: Number(l.planned_amount),
        is_manual: Math.abs(Number(l.planned_amount) - Number(l.suggested_amount)) > 0.01,
      }));
      await saveBudgetMonth({ month, lines: payload, saveAsTemplate: false });
      setSaveStatus("ok");
      setTimeout(() => setSaveStatus(null), 2500);
      onUpdated?.();
      setLines(prev => prev.map(l => ({ ...l, carried_forward: false })));
    } catch (err) {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus(null), 3000);
    }
  }

  async function saveAsTemplate() {
    if (!templateName.trim()) { alert("Veuillez saisir un nom pour le template"); return; }
    try {
      const payload = lines.map(l => ({ subcategory_id: l.subcategory_id, planned_amount: Number(l.planned_amount) }));
      await api.post('/budget/template', { name: templateName.trim(), lines: payload });
      setTemplateName("");
      const r = await api.get('/budget/templates');
      setTemplates(r.data);
    } catch (err) { alert("Erreur enregistrement template : " + (err.response?.data?.error || err.message)); }
  }

  const totalBudget = lines.reduce((s, l) => s + Number(l.planned_amount || 0), 0);
  budgetTargetRef.current = budgetTarget; // toujours à jour pour les handlers async
  const totalSuggestion = lines.reduce((s, l) => s + Number(l.suggested_amount || 0), 0);
  const previewUsers = (contributions.users || []).map(u => ({ ...u, preview: totalBudget * u.ratio }));

  const METHOD_LABEL = {
    smart: 'Auto', average: 'Moyenne', annualized: 'Annualisé',
    fixed: 'Fixe', provision: 'Provision', saving: 'Épargne', loan: 'Prêt', none: 'Ignoré',
  };

  function calcDetail(line) {
    const f = formatCurrency;
    switch (line.budget_method) {
      case 'smart':    return line.smart_months > 0 ? `${f(line.smart_total)} ÷ ${line.smart_months} mois` : '—';
      case 'average':  return line.budget_months ? `${f(line.avg_total)} ÷ ${line.budget_months} mois` : '—';
      case 'annualized': return `${f(line.annual_total)} ÷ 12 mois`;
      case 'fixed':
      case 'provision': {
        const target = line.provision_target ?? (line.provision_annual_total > 0 ? line.provision_annual_total : null);
        const label = line.provision_target !== null ? '(manuel)' : '(auto)';
        return target ? `${f(target)} ÷ 12 mois ${label}` : '—';
      }
      case 'saving': {
        const remaining = Math.max(0, (line.saving_target || 0) - (line.saved_amount || 0));
        return line.saving_target ? `Reste ${f(remaining)} / cible ${f(line.saving_target)}` : '—';
      }
      case 'loan': {
        const repaid = line.saved_amount || 0;
        const total = line.saving_target || 0;
        const remaining = Math.max(0, total - repaid);
        return total ? `${f(repaid)} remboursé / ${f(total)} — reste ${f(remaining)}` : '—';
      }
      default: return '';
    }
  }

  const grouped = lines.reduce((acc, line) => {
    if (!acc[line.category_name]) acc[line.category_name] = [];
    acc[line.category_name].push(line);
    return acc;
  }, {});

  const allCats = Object.keys(grouped);
  const allCollapsed = allCats.length > 0 && allCats.every(c => collapsedCats.has(c));

  const thStyle = {
    padding: "10px 14px", fontSize: 11, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-soft)"
  };

  const Colgroup = () => (
    <colgroup>
      <col />
      <col style={{ width: 120 }} />
      <col style={{ width: 150 }} />
      <col style={{ width: 40 }} />
    </colgroup>
  );

  if (loading) return <div style={{ color: "var(--text-soft)", padding: 24 }}>Chargement...</div>;

  return (
    <div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Préparer budget {month}</h2>
      </div>

      {/* ACTIONS */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20, alignItems: "center" }}>
        <button
          className="btn btn-primary"
          onClick={saveLines}
          disabled={saveStatus === "saving"}
          style={{ padding: "10px 20px" }}
        >
          {saveStatus === "saving" ? "Sauvegarde…" : "Sauvegarder"}
        </button>
        {saveStatus === "ok" && <span style={{ color: "var(--success)", fontWeight: 600, fontSize: 14 }}>✓ Sauvegardé</span>}
        {saveStatus === "error" && <span style={{ color: "var(--danger)", fontWeight: 600, fontSize: 14 }}>✗ Erreur</span>}

        <div style={{ width: 1, height: 28, background: "var(--border)", margin: "0 4px" }} />

        <button className="btn btn-ghost" onClick={resetAllToSuggestions} style={{ fontSize: 12 }}>
          ↺ Toutes les suggestions
        </button>

        <div style={{ width: 1, height: 28, background: "var(--border)", margin: "0 4px" }} />

        <select
          value={selectedTemplate}
          onChange={e => { setSelectedTemplate(e.target.value); loadTemplate(e.target.value); }}
          style={{ minWidth: 180 }}
        >
          <option value="">— Charger un template —</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {selectedTemplate && (
          <button
            className="btn btn-danger"
            onClick={deleteTemplate}
            title="Supprimer ce template"
            style={{ padding: "8px 10px" }}
          >🗑</button>
        )}

        <div style={{ width: 1, height: 28, background: "var(--border)", margin: "0 4px" }} />

        <input
          type="text"
          value={templateName}
          onChange={e => setTemplateName(e.target.value)}
          placeholder="Nom du template…"
          style={{ minWidth: 180 }}
        />
        <button className="btn" onClick={saveAsTemplate}>Enregistrer comme template</button>
      </div>

      {/* BARRE STICKY */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "var(--bg)", paddingBottom: 4 }}>
        <div style={{
          background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
          padding: "12px 20px", display: "flex", alignItems: "center", gap: 0,
          boxShadow: "var(--shadow-md)",
        }}>

          {/* ZONE 1 — Enveloppe budget */}
          <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingRight: 20 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Enveloppe
            </span>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <input
                type="number"
                value={budgetTarget}
                onChange={e => setBudgetTarget(e.target.value)}
                onBlur={async e => {
                  await api.put("/budget/month/target", { month, budget_target: e.target.value });
                }}
                placeholder="Budget cible"
                style={{
                  width: 100, fontWeight: 800, fontSize: 18,
                  border: "none", borderBottom: "2px solid var(--primary)", borderRadius: 0,
                  padding: "0 2px", background: "transparent", color: "var(--text)",
                  outline: "none",
                }}
              />
              {budgetTarget !== "" && budgetTarget !== null && (
                <span style={{
                  fontSize: 12, fontWeight: 700, padding: "1px 7px", borderRadius: 20,
                  background: totalBudget > Number(budgetTarget) ? "#fee2e2" : "#dcfce7",
                  color: totalBudget > Number(budgetTarget) ? "var(--danger)" : "var(--success)",
                }}>
                  {totalBudget > Number(budgetTarget) ? "+" : ""}{formatCurrency(totalBudget - Number(budgetTarget))}
                </span>
              )}
            </div>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
              Total lignes : <strong style={{ color: "var(--text)" }}>{formatCurrency(totalBudget)}</strong>
            </span>
          </div>

          {/* Séparateur */}
          <div style={{ width: 1, alignSelf: "stretch", background: "var(--border)", margin: "0 20px" }} />

          {/* ZONE 2 — Reste par personne (bidirectionnel) */}
          <div style={{ display: "flex", gap: 12, flex: 1 }}>
            {incomes.map(u => {
              const ratio = contributions.users?.find(cu => cu.userId === u.id)?.ratio ?? u.contribution_ratio ?? 0;
              const base = budgetTarget !== "" && budgetTarget !== null ? Number(budgetTarget) : totalBudget;
              const perso = (u.average ?? 0) - base * ratio;
              const positive = perso >= 0;
              const isEditing = resteEditing[u.id] !== undefined;

              return (
                <div key={u.id} style={{
                  display: "flex", flexDirection: "column", gap: 2,
                  padding: "8px 14px", borderRadius: 8,
                  background: positive ? "#f0fdf4" : "#fff1f2",
                  borderLeft: `3px solid ${positive ? "var(--success)" : "var(--danger)"}`,
                  minWidth: 130,
                }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Reste à {u.first_name}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={isEditing ? resteEditing[u.id] : formatCurrency(perso)}
                    onFocus={() => {
                      // Afficher le nombre brut pour la saisie
                      setResteEditing(prev => ({ ...prev, [u.id]: String(Math.round(perso)) }));
                    }}
                    onChange={e => {
                      const raw = e.target.value;
                      setResteEditing(prev => ({ ...prev, [u.id]: raw }));
                      // Recalcul inverse : BT = (moyenne - reste) / ratio
                      const val = parseFloat(raw.replace(',', '.'));
                      if (!isNaN(val) && ratio > 0 && u.average != null) {
                        setBudgetTarget(String(Math.round((u.average - val) / ratio)));
                      }
                    }}
                    onBlur={async () => {
                      // Supprimer le mode édition → revenir à l'affichage formaté
                      setResteEditing(prev => { const n = { ...prev }; delete n[u.id]; return n; });
                      // Sauvegarder via ref pour avoir la valeur à jour (évite closure stale)
                      const current = budgetTargetRef.current;
                      if (current !== "" && current !== null) {
                        await api.put("/budget/month/target", { month, budget_target: current });
                      }
                    }}
                    style={{
                      width: 120, fontWeight: 800, fontSize: 18,
                      color: positive ? "var(--success)" : "var(--danger)",
                      border: "none",
                      borderBottom: isEditing ? "2px solid var(--primary)" : "2px solid transparent",
                      borderRadius: 0, padding: "0 2px",
                      background: "transparent", outline: "none",
                      cursor: "text", lineHeight: 1.1,
                    }}
                  />
                  {u.average != null && (
                    <span style={{ fontSize: 10, color: "var(--text-muted)" }}>
                      sur moy. {formatCurrency(u.average)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Séparateur */}
          <div style={{ width: 1, alignSelf: "stretch", background: "var(--border)", margin: "0 20px" }} />

          {/* ZONE 3 — Actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
            <button
              className="btn btn-primary"
              onClick={saveLines}
              disabled={saveStatus === "saving"}
              style={{ fontSize: 13, padding: "7px 18px", whiteSpace: "nowrap" }}
            >
              {saveStatus === "saving" ? "…" : saveStatus === "ok" ? "✓ Sauvegardé" : saveStatus === "error" ? "✗ Erreur" : "Sauvegarder"}
            </button>
            <button
              className="btn"
              style={{ fontSize: 11, padding: "3px 10px", whiteSpace: "nowrap" }}
              onClick={() => setCollapsedCats(allCollapsed ? new Set() : new Set(allCats))}
            >
              {allCollapsed ? "Tout développer" : "Tout réduire"}
            </button>
          </div>

        </div>

        {/* En-tête aligné */}
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", background: "#f9fafb", borderBottom: "1px solid var(--border)", pointerEvents: "none" }}>
          <Colgroup />
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "left" }}>Sous-catégorie</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Suggestion</th>
              <th style={{ ...thStyle, textAlign: "right" }}>Nouveau budget</th>
              <th />
            </tr>
          </thead>
        </table>
      </div>

      {/* TABLEAU */}
      <div className="table-wrapper" style={{ overflow: "visible", marginTop: 0, borderTop: "none", borderRadius: "0 0 var(--radius-md) var(--radius-md)" }}>
        <table style={{ tableLayout: "fixed" }}>
          <Colgroup />
          <thead style={{ display: "none" }}>
            <tr><th>Sous-catégorie</th><th>Suggestion</th><th>Budget</th><th></th></tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([category, items]) => {
              const isCollapsed = collapsedCats.has(category);
              const catSuggestion = items.reduce((s, l) => s + l.suggested_amount, 0);
              const catBudget = items.reduce((s, l) => s + Number(l.planned_amount || 0), 0);

              return (
                <React.Fragment key={category}>
                  <tr
                    style={{ background: "var(--border-soft)", cursor: "pointer", userSelect: "none" }}
                    onClick={() => setCollapsedCats(prev => {
                      const next = new Set(prev);
                      next.has(category) ? next.delete(category) : next.add(category);
                      return next;
                    })}
                  >
                    <td style={{ fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-soft)", padding: "8px 14px" }}>
                      <span style={{ marginRight: 8, fontSize: 10 }}>{isCollapsed ? "▸" : "▾"}</span>
                      {category}
                      {isCollapsed && (
                        <span style={{ marginLeft: 10, fontWeight: 400, fontSize: 11, color: "var(--text-muted)", textTransform: "none", letterSpacing: 0 }}>
                          {items.length} sous-catégorie{items.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, fontSize: 13, color: "var(--text-muted)", padding: "8px 14px" }}>
                      {formatCurrency(catSuggestion)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 700, fontSize: 13, color: isCollapsed ? "var(--primary)" : "var(--text-muted)", padding: "8px 14px" }}>
                      {formatCurrency(catBudget)}
                    </td>
                    <td />
                  </tr>

                  {!isCollapsed && items.map(line => {
                    const idx = lines.findIndex(l => l.subcategory_id === line.subcategory_id);
                    const diff = line.planned_amount - line.suggested_amount;
                    const isModified = Math.abs(diff) > 0.01;

                    return (
                      <tr key={line.subcategory_id}>
                        <td>
                          <div style={{ fontWeight: 500 }}>{line.subcategory_name}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, display: "flex", gap: 6, alignItems: "center" }}>
                            <span style={{ background: "var(--border-soft)", borderRadius: 4, padding: "1px 5px", fontWeight: 600 }}>
                              {METHOD_LABEL[line.budget_method] || line.budget_method}
                            </span>
                            <span>{calcDetail(line)}</span>
                          </div>
                          {line.budget_method === 'provision' && (
                            <div style={{ marginTop: 5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Objectif :</span>
                              <input
                                type="number" step="1" min="0"
                                placeholder={line.provision_annual_total > 0 ? `${line.provision_annual_total} (auto)` : "ex: 300"}
                                defaultValue={line.provision_target ?? ""}
                                key={`pt-${line.subcategory_id}-${line.provision_target}`}
                                style={{ width: 110, fontSize: 11, padding: "2px 6px" }}
                                onBlur={e => saveProvisionTarget(line.subcategory_id, e.target.value)}
                              />
                              {line.provision_target !== null && (
                                <button className="btn" style={{ fontSize: 10, padding: "1px 6px", lineHeight: 1.4 }}
                                  onClick={() => saveProvisionTarget(line.subcategory_id, "")}>✕</button>
                              )}
                              <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 4 }}>Depuis :</span>
                              <input
                                type="date"
                                defaultValue={line.provision_start_date ?? ""}
                                key={`psd-${line.subcategory_id}-${line.provision_start_date}`}
                                style={{ fontSize: 11, padding: "2px 6px" }}
                                onBlur={e => saveProvisionStartDate(line.subcategory_id, e.target.value)}
                              />
                              {line.provision_start_date && (
                                <button className="btn" style={{ fontSize: 10, padding: "1px 6px", lineHeight: 1.4 }}
                                  onClick={() => saveProvisionStartDate(line.subcategory_id, "")}>✕</button>
                              )}
                            </div>
                          )}
                          {line.budget_method === 'loan' && (
                            <div style={{ marginTop: 5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Capital :</span>
                              <input
                                type="number" step="1" min="0"
                                placeholder="ex: 3000"
                                defaultValue={line.saving_target ?? ""}
                                key={`lt-${line.subcategory_id}-${line.saving_target}`}
                                style={{ width: 100, fontSize: 11, padding: "2px 6px" }}
                                onBlur={e => saveLoanField(line.subcategory_id, 'saving_target', e.target.value)}
                              />
                              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Depuis :</span>
                              <input
                                type="date"
                                defaultValue={line.saving_start_date ?? ""}
                                key={`lsd-${line.subcategory_id}-${line.saving_start_date}`}
                                style={{ fontSize: 11, padding: "2px 6px" }}
                                onBlur={e => saveLoanField(line.subcategory_id, 'saving_start_date', e.target.value || null)}
                              />
                              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Fin :</span>
                              <input
                                type="date"
                                defaultValue={line.saving_target_date ? line.saving_target_date.slice(0,10) : ""}
                                key={`led-${line.subcategory_id}-${line.saving_target_date}`}
                                style={{ fontSize: 11, padding: "2px 6px" }}
                                onBlur={e => saveLoanField(line.subcategory_id, 'saving_target_date', e.target.value || null)}
                              />
                            </div>
                          )}
                        </td>

                        <td style={{ textAlign: "right", color: "var(--text-muted)", fontSize: 13 }}>
                          {formatCurrency(line.suggested_amount)}
                        </td>

                        <td style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                            {line.carried_forward && !isModified && (
                              <span title="Repris du mois précédent" style={{ fontSize: 11, color: "var(--text-muted)" }}>↩</span>
                            )}
                            {isModified && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: diff > 0 ? "var(--warning)" : "var(--primary)" }}>
                                {diff > 0 ? "▲" : "▼"}
                              </span>
                            )}
                            <input
                              type="number"
                              value={line.planned_amount}
                              onChange={e => updateValue(idx, e.target.value)}
                              style={{
                                width: 110, textAlign: "right", fontWeight: 600,
                                background: isModified ? (diff > 0 ? "#fffbeb" : "#fef2f2")
                                           : line.carried_forward ? "#f0f7ff"
                                           : "var(--border-soft)",
                                border: "1px solid var(--border)"
                              }}
                            />
                          </div>
                        </td>

                        <td style={{ textAlign: "center" }}>
                          {isModified && (
                            <button
                              className="btn btn-ghost"
                              onClick={() => resetToSuggestion(idx)}
                              title="Remettre à la suggestion"
                              style={{ padding: "2px 6px", fontSize: 14, color: "var(--text-muted)", lineHeight: 1 }}
                            >↺</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {!isCollapsed && (
                    <tr style={{ background: "var(--primary-soft)" }}>
                      <td style={{ fontWeight: 600, fontSize: 13, padding: "8px 14px" }}>Sous-total {category}</td>
                      <td style={{ textAlign: "right", fontWeight: 600, fontSize: 13, color: "var(--text-muted)" }}>
                        {formatCurrency(catSuggestion)}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "var(--primary)", fontSize: 13, paddingRight: 14 }}>
                        {formatCurrency(catBudget)}
                      </td>
                      <td />
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}
